import React, { useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { Button } from "rsuite";
import TrendingUpIcon from "../../../../assets/img/action-center/trending-up.svg";
import DollarIcon from "../../../../assets/img/action-center/dollar.svg";
import ChevronRightSmIcon from "../../../../assets/img/action-center/chevron-right-sm.svg";
import PromotionCreatePromo from "../../../../pages/promotions/PromotionCreatePromo";
import { deepClone } from "../../../../constants/utils";
import {
  deepMergeWithTypes,
  sanitizeRuleVO,
} from "../../../../utils/promoObjectMapper";
import {
  mapDerivedPromoDetailToPromo,
  extractCalculationType,
} from "../../../../utils/derivedRatesMapper";
import { useIsPilotHotel } from "../../../../contexts/PropertyStoreContext";
import useCreatePromoDrawer from "../../../../hooks/useCreatePromoDrawer";
import { PROMO_OBJECT } from "../../../../pages/promotions/constants";
import { REVENUE_THRESHOLD } from "../../../../constants/recommendations";
import { Can, P } from "../../../../rbac";
import useHeapCardViewed from "../../../../hooks/useHeapCardViewed";
import "./PromoCard.scss";

const PROMO_CATEGORY_MAP = {
  ALIGNMENT_INTELLIGENCE: "alignment",
  DOW: "DOW",
  LOS: "LOS",
  LEAD_TIME: "LEAD_TIME",
};

const formatRevenue = (revenueValue, revenuePct) => {
  if (revenueValue >= REVENUE_THRESHOLD) {
    const rounded = Math.round(revenueValue);
    return `$${rounded.toLocaleString()}/month`;
  }
  if (revenuePct != null) {
    const rounded = Math.round(revenuePct);
    return `+${rounded}%/month`;
  }
  return null;
};

const SHORT_TO_FULL = {
  Mon: "Monday",
  Tue: "Tuesday",
  Wed: "Wednesday",
  Thu: "Thursday",
  Fri: "Friday",
  Sat: "Saturday",
  Sun: "Sunday",
};

const shortenRange = (dayRange) => {
  // Single day: expand abbreviation to full name (e.g. "Sat" → "Saturday")
  if (!dayRange.includes("-")) {
    return SHORT_TO_FULL[dayRange] || dayRange;
  }
  // Range: keep abbreviations, replace hyphen with en-dash
  return dayRange.replace("-", "–");
};

const buildAlignmentContent = (promo, revenue, bookings) => {
  const currentScore = Math.round((promo.currentAlignmentRate || 0) * 100);
  const targetScore = Math.round((promo.futureAlignmentRate || 0.9) * 100);
  return {
    badgeLabel: "Revenue Opportunity",
    badgeVariant: "green",
    title: "Improve your Alignment Score",
    description:
      "An Alignment Score above 80% drives more direct bookings. A targeted promo can help close the gap.",
    revenue,
    bookings,
    rowTop: {
      dotColor: "#2BD286",
      label: "Target score",
      value: `${targetScore}%`,
    },
    rowBottom: {
      dotColor: "#324051",
      label: "Your current score",
      value: `${currentScore}%`,
    },
  };
};

const buildDowContent = (promo, revenue, bookings) => {
  const dayRange = promo.dayRange || "weekday";
  const shortRange = shortenRange(dayRange);
  return {
    badgeLabel: "Revenue Opportunity",
    badgeVariant: "green",
    title: `Improve your ${shortRange} Share`,
    description: `Your ${shortRange} share is below the city average. Close this gap with a targeted Day of Week promo.`,
    revenue,
    bookings,
    rowTop: {
      dotColor: "#2BD286",
      label: "City average",
      value: promo.cityAvgShare
        ? `${Math.abs(Math.round(promo.cityAvgShare * 100))}%`
        : "--",
    },
    rowBottom: {
      dotColor: "#324051",
      label: "Your average",
      value: promo.propertyAvgShare
        ? `${Math.abs(Math.round(promo.propertyAvgShare * 100))}%`
        : "--",
    },
  };
};

const buildLosContent = (promo, revenue, bookings) => {
  const yourLos = promo.avgLosProperty || promo.yourAvgLos || promo.avgLos;
  const cityLos = promo.avgLosCity || promo.cityAvgLos;
  return {
    badgeLabel: "Revenue Opportunity",
    badgeVariant: "green",
    title: "Increase your Length of Stay",
    description:
      "Your average guest stay is shorter than the city average. A Length of Stay promo can increase revenue per booking.",
    revenue,
    bookings,
    rowTop: {
      dotColor: "#2BD286",
      label: "City average LoS (nights)",
      value: cityLos ? `${cityLos}` : "--",
    },
    rowBottom: {
      dotColor: "#324051",
      label: "Your average LoS",
      value: yourLos ? `${yourLos}` : "--",
    },
  };
};

const buildLeadTimeContent = (promo, revenue, bookings) => {
  const yourLt = promo.avgLeadTimeProperty;
  const cityLt = promo.avgLeadTimeCity;
  return {
    badgeLabel: "Longer Lead Time Opportunity",
    badgeVariant: "blue",
    title: "Increase your Lead Time",
    description:
      "Your booking lead time is shorter than the city average. A longer lead time promo can help drive earlier bookings.",
    revenue,
    bookings,
    rowTop: {
      dotColor: "#2BD286",
      label: "City average LT (days)",
      value: cityLt ? `${cityLt}` : "--",
    },
    rowBottom: {
      dotColor: "#324051",
      label: "Your average LT (days)",
      value: yourLt ? `${yourLt}` : "--",
    },
  };
};

const CARD_CONTENT_BUILDERS = {
  ALIGNMENT_INTELLIGENCE: buildAlignmentContent,
  DOW: buildDowContent,
  LOS: buildLosContent,
  LEAD_TIME: buildLeadTimeContent,
};

const PromoCard = ({
  data = [],
  onDismiss,
  selectedProperty,
  onPromoUsed,
  cardPosition,
  hotelName,
}) => {
  const isPilotHotel = useIsPilotHotel();
  const promoType_ = data[0]?.type;
  const cardRef = useHeapCardViewed(
    promoType_ ? `promo_${promoType_.toLowerCase()}` : "promo",
    cardPosition,
    hotelName
  );
  const drawer = useCreatePromoDrawer();
  const handlePromoSaved = useCallback(() => {
    onPromoUsed?.();
  }, [onPromoUsed]);

  const promoGroup = data[0];
  const promoType = promoGroup?.type;
  const promo = promoGroup?.data?.[0];

  const cardContent = useMemo(() => {
    if (!promo) return null;
    const bookings = promo.estimatedBookingsPerMonth;
    const revenueValue = promo.expectedRevenueIncrease ?? 0;
    const revenue = formatRevenue(
      revenueValue,
      promo.expectedRevenueIncreasePct
    );
    const revenueIsPct = revenueValue < REVENUE_THRESHOLD;
    const builder = CARD_CONTENT_BUILDERS[promoType];
    if (!builder) return null;
    const content = builder(promo, revenue, bookings);
    return content ? { ...content, revenueIsPct } : null;
  }, [promo, promoType]);

  if (!promo || !cardContent) return null;

  const handleCreatePromo = async () => {
    // loading handled by handleSetLoading passed to drawer

    // Parse promoObject (string or object); pilot hotels use the derived-rate object
    let promoObject = isPilotHotel ? promo.promoObjectDr : promo.promoObject;
    if (typeof promoObject === "string") {
      try {
        promoObject = JSON.parse(promoObject);
      } catch {
        promoObject = null;
      }
    }

    if (isPilotHotel) {
      // ── Pilot: the derived-rate snapshot lives under promoObject.snapshot (D1) ──
      const snapshot = promoObject?.snapshot || {};
      const mapped = mapDerivedPromoDetailToPromo(snapshot); // D3 reuse

      // Treat as a NEW event → force CREATE, not UPDATE (D4)
      delete mapped._derivedRateMeta;

      mapped.suggestionPromoId = promo.promoId;
      mapped.promoCategory = PROMO_CATEGORY_MAP[promoType] || promoType;

      drawer.open(mapped, extractCalculationType(snapshot)); // D5
    } else {
      // ── Legacy (non-pilot) path — unchanged ──
      // Deep merge with PROMO_OBJECT template to coerce string types to match expected types
      const basePromo = deepClone(PROMO_OBJECT);
      const merged = deepMergeWithTypes(basePromo, promoObject);

      // Ensure all ruleVO fields have correct types for the backend DTO
      sanitizeRuleVO(merged.rateRule?.ruleVO);

      // DOW promos need "Select days of week" mode (discountType: 1) to show day-specific inputs
      if (promoType === "DOW" && merged.rateRule?.ruleVO?.discountVO) {
        merged.rateRule.ruleVO.discountVO.discountType = 1;
      }

      drawer.open({
        ...merged,
        suggestionPromoId: promo.promoId,
        promoCategory: PROMO_CATEGORY_MAP[promoType] || promoType,
      });
    }

    await drawer.loadRatePlansAndRooms(selectedProperty?.propertyId);
  };

  const handleMaybeLater = () => {
    if (onDismiss) {
      onDismiss();
    }
  };

  return (
    <>
      <div ref={cardRef} className="promo-card">
        <div
          className={`promo-card-badge promo-card-badge--${cardContent.badgeVariant}`}
        >
          <img src={TrendingUpIcon} alt="" className="badge-icon" />
          <span>{cardContent.badgeLabel}</span>
        </div>

        <h3 className="promo-card-title">{cardContent.title}</h3>

        <p className="promo-card-description">{cardContent.description}</p>

        <div className="promo-card-data">
          {cardContent.revenue && (
            <div className="promo-card-revenue">
              <div className="revenue-left">
                <span className="revenue-icon">
                  <img src={DollarIcon} alt="" />
                </span>
                <div className="revenue-text">
                  <span className="revenue-label">INCREASE REVENUE</span>
                  <span className="revenue-value">{cardContent.revenue}</span>
                </div>
              </div>
              {cardContent.bookings && (
                <div className="revenue-right">
                  <div className="revenue-bookings">
                    <img
                      src={TrendingUpIcon}
                      alt=""
                      className="revenue-trending"
                    />
                    <span className="bookings-count">
                      {cardContent.bookings}
                    </span>
                  </div>
                  <span className="bookings-label">Bookings/month</span>
                </div>
              )}
            </div>
          )}

          <div className="promo-card-comparison">
            <div className="comparison-row">
              <div className="comparison-label">
                <span
                  className="comparison-dot"
                  style={{ backgroundColor: cardContent.rowTop.dotColor }}
                />
                <span>{cardContent.rowTop.label}</span>
              </div>
              <div className="comparison-values">
                <span className="comparison-value">
                  {cardContent.rowTop.value}
                </span>
              </div>
            </div>
            <div className="comparison-row">
              <div className="comparison-label">
                <span
                  className="comparison-dot"
                  style={{ backgroundColor: cardContent.rowBottom.dotColor }}
                />
                <span>{cardContent.rowBottom.label}</span>
              </div>
              <div className="comparison-values">
                <span className="comparison-value">
                  {cardContent.rowBottom.value}
                </span>
              </div>
            </div>
            {promoType !== "ALIGNMENT_INTELLIGENCE" && (
              <p className="promo-card-averages-note">
                * Averages based on bookings from the past 30 days across Hotel
                Trader
              </p>
            )}
          </div>
        </div>

        <div className="promo-card-actions">
          <div className="promo-card-actions-left">
            <Button
              id="action-promo-later"
              appearance="link"
              className="maybe-later-btn"
              onClick={handleMaybeLater}
            >
              Maybe Later
            </Button>
          </div>
          <Can permission={P.Home.ActionCenter.CreatePromo}>
            <Button
              id="action-promo-create"
              appearance="ghost"
              className="create-promo-btn"
              onClick={handleCreatePromo}
            >
              {"Create Promo"}{" "}
              <img src={ChevronRightSmIcon} alt="" className="btn-chevron" />
            </Button>
          </Can>
        </div>
      </div>
      {drawer.loader}
      {drawer.isOpen && (
        <PromotionCreatePromo
          {...drawer.drawerProps}
          property={selectedProperty}
          isPilotHotel={isPilotHotel}
          setIsGetPromos={handlePromoSaved}
        />
      )}
    </>
  );
};

PromoCard.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.oneOf([
        "DOW",
        "LOS",
        "ALIGNMENT_INTELLIGENCE",
        "LEAD_TIME",
      ]).isRequired,
      data: PropTypes.arrayOf(
        PropTypes.shape({
          recommendationId: PropTypes.number,
          propertyId: PropTypes.number,
          hotelCrsCode: PropTypes.string,
          promoId: PropTypes.string,
          promoObject: PropTypes.string,
          dayRange: PropTypes.string,
          avgShareDelta: PropTypes.number,
          expiryDate: PropTypes.string,
          url: PropTypes.string,
          hasUsed: PropTypes.bool,
          currentAlignmentRate: PropTypes.number,
          avgLosProperty: PropTypes.number,
          avgLosCity: PropTypes.number,
          avgLeadTimeProperty: PropTypes.number,
          avgLeadTimeCity: PropTypes.number,
          propertyGmvLast30Days: PropTypes.number,
          expectedRevenueIncrease: PropTypes.number,
          estimatedBookingsPerMonth: PropTypes.number,
          percentDifference: PropTypes.number,
        })
      ),
    })
  ).isRequired,
  onDismiss: PropTypes.func,
  selectedProperty: PropTypes.object,
  onPromoUsed: PropTypes.func,
  cardPosition: PropTypes.number,
  hotelName: PropTypes.string,
};

export default PromoCard;
